import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import { useTheme } from '@mui/material';
import Button from '@mui/material/Button';
import CardActions from '@mui/material/CardActions';
import { alpha } from '@mui/material/styles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { approveProcedure, unapproveProcedure } from 'api/procedures';
import { Procedure } from 'interfaces/procedure';
import React from 'react';
import { useEncodedFilters } from 'utils/useEncodedFilters';
interface Props {
  procedure: Procedure;
}

const ApprovalWidget = ({ procedure }: Props) => {
  const theme = useTheme();
  const { encodedFilters } = useEncodedFilters();

  const queryClient = useQueryClient();

  const approveMutation = useMutation(approveProcedure, {
    onSuccess: () => {
      queryClient.invalidateQueries(['procedures', encodedFilters]);
    },
  });

  const unapproveMutation = useMutation(unapproveProcedure, {
    onSuccess: () => {
      queryClient.invalidateQueries(['procedures', encodedFilters]);
    },
  });

  const handleApproveProcedure = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (confirm('Are you sure you want to approve this procedure?')) {
      approveMutation.mutate(procedure.id);
    }
  };

  const handleUnapproveProcedure = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (confirm('Are you sure you want to disapprove this procedure?')) {
      unapproveMutation.mutate(procedure.id);
    }
  };

  const isApproved = Boolean(procedure.internallyApproved);
  const actionLabel = isApproved ? 'Disapprove' : 'Approve';
  const ActionIcon = isApproved ? BlockOutlinedIcon : TaskAltOutlinedIcon;
  const handleActionClick = isApproved ? handleUnapproveProcedure : handleApproveProcedure;

  return (
    <CardActions sx={{ p: 0, pt: 1, mt: 'auto', justifyContent: 'flex-start' }}>
      <Button
        size="small"
        variant={isApproved ? 'outlined' : 'text'}
        color={isApproved ? 'inherit' : 'primary'}
        startIcon={<ActionIcon sx={{ fontSize: 16 }} />}
        onClick={handleActionClick}
        sx={{
          minWidth: 0,
          px: 1,
          py: 0.375,
          borderRadius: 999,
          fontSize: '0.8125rem',
          fontWeight: 600,
          letterSpacing: 0,
          lineHeight: 1.2,
          textTransform: 'none',
          alignSelf: 'flex-start',
          color: isApproved ? theme.palette.text.secondary : theme.palette.primary.main,
          borderColor: isApproved
            ? alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.14 : 0.1)
            : 'transparent',
          backgroundColor: isApproved
            ? alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.04 : 0.02)
            : alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.08),
          '& .MuiButton-startIcon': {
            marginRight: theme.spacing(0.5),
            marginLeft: 0,
          },
          '&:hover': {
            borderColor: isApproved
              ? alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.22 : 0.16)
              : 'transparent',
            backgroundColor: isApproved
              ? alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.08 : 0.05)
              : alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.14),
          },
        }}
      >
        {actionLabel}
      </Button>
    </CardActions>
  );
};

export default ApprovalWidget;
